import React from 'react';
import { CHAIN_POINT_NAMES } from '../../engine/keywordAbilities';
import { useBattle } from '../context';

/**
 * ChainConfirmationModal
 *
 * 刹那詠唱のチェーン確認ダイアログ
 * 攻撃宣言時やバトルフェイズ開始時に表示され、
 * 非アクティブプレイヤーが刹那詠唱カードを発動するかを選択する
 *
 * Props: なし（全て Context から取得）
 */
const ChainConfirmationModal: React.FC = () => {
  // Context から状態を取得
  const {
    // 手札・SP データ
    p1Hand,
    p2Hand,
    p1ActiveSP,
    p2ActiveSP,
    // setsuna hook
    setsuna,
  } = useBattle();

  // setsuna hook から関数・状態を展開
  const {
    chainConfirmation,
    setsunaPendingCard,
    setSetsunaPendingCard,
    filterActivatableSetsunaCards,
    getSetsunaActualCost,
    activateSetsunaInChain,
    skipChainConfirmation,
  } = setsuna;
  // chainConfirmation が null の場合はレンダリングしない
  if (!chainConfirmation) {
    return null;
  }

  // 発動可能なカードを取得
  const activatableCards = filterActivatableSetsunaCards(
    chainConfirmation.askingPlayer === 1 ? p1Hand : p2Hand,
    chainConfirmation.askingPlayer === 1 ? p1ActiveSP : p2ActiveSP,
    chainConfirmation.askingPlayer
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '600px',
          width: '90vw',
          border: '3px solid #9c6bff',
          boxShadow: '0 0 40px rgba(156, 107, 255, 0.5)',
        }}
      >
        {/* ヘッダー */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{
            margin: 0,
            color: '#9c6bff',
            fontSize: '1.5rem',
            textShadow: '0 0 10px rgba(156, 107, 255, 0.5)',
          }}>
            ⚡ 【刹那詠唱】チェーン確認
          </h2>
          <p style={{
            margin: '8px 0 0 0',
            color: '#aaa',
            fontSize: '0.9rem'
          }}>
            {CHAIN_POINT_NAMES[chainConfirmation.chainPoint]}時
          </p>
        </div>

        {/* 対象プレイヤー表示 */}
        <div style={{
          textAlign: 'center',
          marginBottom: '16px',
          color: chainConfirmation.askingPlayer === 1 ? '#4da6ff' : '#ff6b6b',
          fontWeight: 'bold',
          fontSize: '1.2rem',
        }}>
          プレイヤー{chainConfirmation.askingPlayer}、刹那詠唱を発動しますか？
        </div>

        {/* 攻撃情報表示（攻撃宣言時の場合） */}
        {chainConfirmation.context?.attacker && (
          <div style={{
            background: 'rgba(255,100,100,0.15)',
            border: '1px solid rgba(255,100,100,0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            <span style={{ color: '#ff6b6b' }}>⚔️ 攻撃宣言: </span>
            <span style={{ color: '#fff', fontWeight: 'bold' }}>
              {chainConfirmation.context.attacker.name}
            </span>
          </div>
        )}

        {/* 発動可能なカード一覧 */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ color: '#aaa', fontSize: '0.85rem', marginBottom: '8px' }}>
            発動可能な刹那詠唱カード:
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            maxHeight: '200px',
            overflowY: 'auto',
            padding: '4px',
          }}>
            {activatableCards.map(card => (
              <div
                key={card.uniqueId}
                onClick={() => setSetsunaPendingCard(
                  setsunaPendingCard?.uniqueId === card.uniqueId ? null : card
                )}
                style={{
                  background: setsunaPendingCard?.uniqueId === card.uniqueId
                    ? 'linear-gradient(135deg, #9c6bff, #7b4bd4)'
                    : 'linear-gradient(135deg, #2a2a4a, #1a1a3a)',
                  border: setsunaPendingCard?.uniqueId === card.uniqueId
                    ? '2px solid #fff'
                    : '1px solid #555',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minWidth: '120px',
                  textAlign: 'center',
                }}
              >
                <div style={{
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  marginBottom: '4px',
                }}>
                  {card.name}
                </div>
                <div style={{
                  color: '#ffd700',
                  fontSize: '0.75rem',
                }}>
                  コスト: {getSetsunaActualCost(card, chainConfirmation.askingPlayer)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 選択中のカード詳細 */}
        {setsunaPendingCard && (
          <div style={{
            background: 'rgba(156, 107, 255, 0.15)',
            border: '1px solid rgba(156, 107, 255, 0.4)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
          }}>
            <div style={{
              color: '#9c6bff',
              fontWeight: 'bold',
              marginBottom: '6px',
            }}>
              選択中: {setsunaPendingCard.name}
            </div>
            <div style={{
              color: '#ccc',
              fontSize: '0.85rem',
              lineHeight: '1.4',
            }}>
              {setsunaPendingCard.effect}
            </div>
          </div>
        )}

        {/* アクションボタン */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          marginTop: '20px',
        }}>
          {setsunaPendingCard && (
            <button
              onClick={() => activateSetsunaInChain(setsunaPendingCard)}
              style={{
                background: 'linear-gradient(135deg, #9c6bff, #7b4bd4)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                padding: '12px 24px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                boxShadow: '0 0 15px rgba(156, 107, 255, 0.4)',
              }}
            >
              ⚡ {setsunaPendingCard.name}を発動
            </button>
          )}
          <button
            onClick={skipChainConfirmation}
            style={{
              background: 'linear-gradient(135deg, #555, #333)',
              border: '1px solid #666',
              borderRadius: '8px',
              color: '#ccc',
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            発動しない
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChainConfirmationModal;
